import { z } from "zod";

/**
 * Tenant-weite Konfiguration. Liegt unter
 * `.bidi-handel/tenants/<tenantId>/config.json`, gitignored.
 *
 * Wird aktuell vom Bepreisungs-Agent (Phase 5) für `margeProzent` gelesen
 * und vom Angebots-Generator (Phase 6) für die Firma-Daten im
 * Anschreiben/Excel/PDF.
 *
 * Schema bewusst tolerant — wenn die Datei fehlt liefert der Loader Defaults.
 * Pilot-Händler kann später Felder ergänzen ohne dass Bestandsdaten brechen.
 */

export const TenantFirmaSchema = z
  .object({
    name: z.string().optional(),
    strasse: z.string().optional(),
    plz: z.string().optional(),
    ort: z.string().optional(),
    email: z.string().email().optional(),
    telefon: z.string().optional(),
    ustId: z.string().optional(),
  })
  .default({});
export type TenantFirma = z.infer<typeof TenantFirmaSchema>;

/**
 * Tenant-weite Ausschluss-Listen für „Wir bieten generell kein XY an".
 *
 * Nach Phase 4 (kundenartikel-match-agent) checkt ein Post-Match-Step die
 * Top-Kandidaten gegen diese Listen und markiert Positionen, deren bester
 * Treffer in eine ausgeschlossene Warengruppe fällt oder mit einem
 * ausgeschlossenen Artikel-Code-Präfix anfängt, als `nichtAngeboten=true`.
 *
 * Vergleich ist case-insensitive. Bereits gepickte oder bereits als nicht-
 * angeboten markierte Positionen werden NICHT überschrieben.
 */
export const TenantNichtAngebotenSchema = z
  .object({
    /** Warengruppen-Slugs (z.B. ["dach", "fenster"]) — match gegen
     *  kundenartikel.warengruppe des Top-Kandidaten. */
    warengruppen: z.array(z.string()).default([]),
    /** Artikel-Code-Präfixe (z.B. ["DACH-", "FE-"]) — match per startsWith
     *  gegen kundenartikel.code des Top-Kandidaten. */
    artikelPraefixe: z.array(z.string()).default([]),
  })
  .default({ warengruppen: [], artikelPraefixe: [] });
export type TenantNichtAngeboten = z.infer<typeof TenantNichtAngebotenSchema>;

/**
 * Match-Policy — Stellschrauben für den kundenartikel-match-agent.
 *
 * `serviceCapScore` ist der Score-Cap für Positionen mit Dienstleistungs-
 * Verben im LV-Originaltext (z.B. „liefern und aufbringen"). Stammdaten
 * decken nur Material ab — der Service-Teil muss separat bepreist oder
 * ausgeschlossen werden.
 *
 * Default 75 → Position landet bei PRÜFEN, User sieht im Detail-Modal
 * den Hinweis + ein Auto-Klärpunkt wird erstellt. Tenant kann den Wert
 * anpassen, sobald Telemetrie zeigt wie User tatsächlich entscheiden.
 */
export const TenantMatchPolicySchema = z
  .object({
    serviceCapScore: z.number().int().min(0).max(100).default(75),
  })
  .default({ serviceCapScore: 75 });
export type TenantMatchPolicy = z.infer<typeof TenantMatchPolicySchema>;

export const TenantConfigSchema = z.object({
  /** Default-Aufschlag auf den Einkaufs-/Sonder-/Listenpreis in Prozent.
   *  Pro-Position-Override per Konditionsmatrix-Eintrag ist H7-Thema. */
  margeProzent: z.number().min(0).max(500).default(0),
  waehrung: z.string().default("EUR"),
  firma: TenantFirmaSchema,
  nichtAngeboten: TenantNichtAngebotenSchema,
  matchPolicy: TenantMatchPolicySchema,
});
export type TenantConfig = z.infer<typeof TenantConfigSchema>;
